/**
 * Utility functions for working with FHIR QuestionnaireResponse data
 */

import { AccForm } from '../dto/accFormDto'
import { QuestionnaireParser } from '../services/questionnaireParser'
import { ValueSetConcept } from '../types/fhir'

/**
 * Extract the report title from a QuestionnaireResponse JSON string
 * Looks for the "text" field in the first item (id-0)
 *
 * @param responseJson - The QuestionnaireResponse JSON string from FormDto.response
 * @returns The report title (e.g., "Report 1") or null if not found
 */
export function extractReportTitle(
    responseJson?: string | null
): string | null {
    if (!responseJson) {
        return null
    }

    try {
        const response = JSON.parse(responseJson)

        // Navigate to the first item (id-0) which contains the report title
        const firstItem = response?.item?.[0]

        if (firstItem?.text) {
            return firstItem.text
        }

        return null
    } catch (error) {
        console.error('Failed to parse QuestionnaireResponse JSON:', error)
        return null
    }
}

/**
 * Extract the full pathology report text from a QuestionnaireResponse JSON string
 * Looks for the valueString in the item with linkId "id-1"
 *
 * @param responseJson - The QuestionnaireResponse JSON string from FormDto.response
 * @returns The full report text or null if not found
 */
export function extractFullReportText(
    responseJson?: string | null
): string | null {
    if (!responseJson) {
        return null
    }

    try {
        const response = JSON.parse(responseJson)

        // Navigate to the first item (id-0) then find id-1
        const firstItem = response?.item?.[0]
        const textItem = firstItem?.item?.find(
            (item: { linkId?: string }) => item.linkId === 'id-1'
        )

        if (textItem?.answer?.[0]?.valueString) {
            return textItem.answer[0].valueString
        }

        return null
    } catch (error) {
        console.error('Failed to parse QuestionnaireResponse JSON:', error)
        return null
    }
}

/**
 * Resolve display values for form fields using ValueSet lookups.
 *
 * For each field that has a `code` and a corresponding `answerValueSet` in the
 * questionnaire metadata, looks up the display text from the fetched ValueSet
 * concepts and replaces the field's `content` with the resolved display.
 *
 * Falls back to the original `content` if no match is found.
 *
 * @param form - The AccForm with raw content values
 * @param parser - Parsed questionnaire with item metadata
 * @param valueSets - Map of ValueSet URL → concepts
 * @returns A new AccForm with resolved display values
 */
export function resolveFormDisplayValues(
    form: AccForm,
    parser: QuestionnaireParser,
    valueSets: Map<string, ValueSetConcept[]>
): AccForm {
    return {
        ...form,
        sections: form.sections.map((section) => ({
            ...section,
            rows: section.rows.map((row) => ({
                ...row,
                columns: row.columns.map((columnGroup) =>
                    columnGroup.map((field) => {
                        if (!field.linkId) return field

                        const metadata = parser.getItem(field.linkId)
                        if (!metadata?.answerValueSet) return field

                        const concepts = valueSets.get(metadata.answerValueSet)
                        if (!concepts || concepts.length === 0) return field

                        // Multi-select: resolve each coding in the array
                        if (field.codings && field.codings.length > 0) {
                            const resolvedCodings = field.codings.map((c) => {
                                const match = concepts.find(
                                    (vs) =>
                                        vs.code === c.code &&
                                        (!c.system || vs.system === c.system)
                                )
                                return match
                                    ? { ...c, display: match.display }
                                    : c
                            })
                            const resolvedContent = resolvedCodings
                                .map((c) => c.display || c.code)
                                .join(', ')
                            return {
                                ...field,
                                content: resolvedContent,
                                codings: resolvedCodings,
                            }
                        }

                        // Single select: resolve from code
                        if (!field.code) return field

                        const match = concepts.find(
                            (c) =>
                                c.code === field.code &&
                                (!field.system || c.system === field.system)
                        )

                        if (!match) return field

                        return { ...field, content: match.display }
                    })
                ),
            })),
        })),
    }
}
